import type { Trigger } from './definitions/triggers';
import type TriggerVehicleGroup from './definitions/vehicleGroups';
import type TriggerVehicle from './definitions/vehicles';

import type { PlainGeofence } from '@cobliteam/common-types/src/geofenceV3';
import type { UUID } from '@cobliteam/common-types/src/identifier';
import type { PageableContent } from '@cobliteam/common-types/src/pagination';
import type { Vehicle, VehicleGroup } from '@cobliteam/common-types/src/vehicle';

interface ParseTriggersOptions {
  geofences?: PlainGeofence[];
  vehicles?: Vehicle[];
  vehicleGroups?: VehicleGroup[];
}

const getVehicles = (vehicleIds: UUID[], vehicles: Vehicle[]): TriggerVehicle[] => {
  return vehicleIds.reduce((acc: TriggerVehicle[], vehicleId: UUID) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);

    if (vehicle) {
      acc.push({
        id: vehicle.id,
        label: vehicle.label,
        licensePlate: vehicle.licensePlate || '',
        vehicleGroups: vehicle.groups || [],
        model: vehicle.model.brand || '',
        groupPath: vehicle.groupPath || '',
      });
    }

    return acc;
  }, []);
};

const getVehicleGroups = (vehicleGroupIds: UUID[], vehicleGroups: VehicleGroup[]): TriggerVehicleGroup[] => {
  return vehicleGroupIds.reduce((acc: TriggerVehicleGroup[], vehicleGroupId: UUID) => {
    const vehicleGroup = vehicleGroups.find((g) => g.id === vehicleGroupId);

    if (vehicleGroup) {
      acc.push({
        id: vehicleGroup.id,
        name: vehicleGroup.name,
        path: vehicleGroup.path || '',
      });
    }

    return acc;
  }, []);
};

const parseTrigger = (trigger: any, data: ParseTriggersOptions): Trigger => {
  const { geofenceIds, vehicleIds, vehicleGroupIds } = trigger;
  const geofences = data.geofences?.filter((geofence) => geofenceIds.includes(geofence.id));
  const vehicles = getVehicles(vehicleIds, data.vehicles || []);
  const vehicleGroups = getVehicleGroups(vehicleGroupIds, data.vehicleGroups || []);
  return { geofences, vehicles, vehicleGroups, ...trigger };
};

const parseTriggers = (triggers: PageableContent<any>, { geofences = [], vehicles = [], vehicleGroups = [] }: ParseTriggersOptions): PageableContent<Trigger> => {
  const parsedTriggers = triggers.content.map((trigger) => parseTrigger(trigger, { geofences, vehicles, vehicleGroups }));

  return {
    ...triggers,
    content: parsedTriggers,
  };
};

export { parseTrigger, parseTriggers };
